import * as React from 'react';
import { Switch, RouteComponentProps } from 'react-router-dom';
import { observer } from 'mobx-react';

import { ApiStore } from '../../stores/ApiStore';
import { UiStore } from '../../stores/UiStore';
import { inject, bind } from '../../utils/decorators';
import { IAuftrag } from '../../models/ApiTypes';
import AuftragDetails from './AuftragDetails';
import VorgangDetailRouter from '../vorgaenge/VorgangDetailRouter';
import PrivateRoute from '../site/PrivateRoute';
import AuftragAnlegen from './AuftragAnlegenSES';
import AuftragDienstleistungAnlegen from './AuftragDienstleistungAnlegen';
import { AuftragStore } from '../../stores/AuftragStore';
import ObjektAuswahl from '../objekte/ObjektAuswahl';
import ObjektAnlegenSEP from '../objekte/ObjektAnlegenSEP';
import { IObjekt, AdressArt } from '../../models/ApiTypes';
import ObjektAnlegenAGT from '../objekte/ObjektAnlegenAGT';
import BreadCrumbs from '../site/BreadCrumbs';
import AuftragAdresseAendern from './adressen/AuftragAdresseAendern';
import ObjektAnlegenBPS from '../objekte/ObjektAnlegenBPS';
import LadeFortschritt from '../site/LadeFortschritt';
import AuftragDetailsAendern from './AuftragDetailsAendern';


interface IAuftragDetailRouterProps {
  auftragId: string;
}

@observer
export default class AuftragDetailRouter extends React.Component<RouteComponentProps<IAuftragDetailRouterProps>, {}> {

  @inject(ApiStore) apiStore: ApiStore;
  @inject(AuftragStore) auftragStore: AuftragStore;
  @inject(UiStore) uiStore: UiStore;

  componentDidMount() {
    this.loadAuftrag();
  }

  async loadAuftrag() {
    const { auftragId } = this.props.match.params;
    this.auftragStore.loadAuftrag(auftragId);
  }

  @bind async handleAuftragNeuladen() {
    this.loadAuftrag();
  }

  @bind async handleAddDienstleistung(dienstleistung: string) {
    const { auftragId } = this.props.match.params;
    const response = await this.apiStore.auftragDienstleistungAnlegen(auftragId, dienstleistung);
    if (response.ok) {
      this.uiStore.navigateTo(this.props.match.url);
      this.loadAuftrag();
    }
  }

  @bind async handleFolgeAuftragAnlegen(auftrag: IAuftrag) {
    const response = await this.apiStore.auftragFolgeaAuftragAnlegen(auftrag.anr);
    if (response.ok) {
      this.uiStore.navigateTo(`/auftrag/${response.json.success}`, true);
      // Der Router lädt den Auftrag nicht automatisch neu, deshalb manuellen Reload forcieren
      // @todo: Besseren/saubereren Weg finden. History ist trotzdem falsch.
      location.reload();
    }
  }

  @bind async handleAuftragAbschliessen(auftrag: IAuftrag) {
    const response = await this.apiStore.auftragAbschliessen(auftrag.anr);
    if (response.ok) {
      this.loadAuftrag();
    }
  }

  @bind async handleAuftragZurueckstellen(auftrag: IAuftrag) {
    const response = await this.apiStore.auftragZurueckstellen(auftrag.anr);
    if (response.ok) {
      this.loadAuftrag();
    }
  }

  @bind async handleAuftragInArbeit(auftrag: IAuftrag) {
    const response = await this.apiStore.auftragInArbeit(auftrag.anr);
    if (response.ok) {
      this.loadAuftrag();
    }
  }

  @bind async handleObjektAuswahl(objekt: IObjekt) {
    const { auftragId } = this.props.match.params;
    const response = await this.apiStore.auftragObjektSetzen(auftragId, objekt.objektguid);
    if (response.ok && response.json.success) {
      this.loadAuftrag();
      this.uiStore.navigateTo(this.props.match.url);
    }
  }

  @bind async handleAddObjekt(objekt: Partial<IObjekt>) {
    const { auftragId } = this.props.match.params;
    const response = await this.apiStore.objektAnlegen(objekt, auftragId);
    if (response.ok && response.json.success) {
      this.loadAuftrag();
      this.uiStore.navigateTo(this.props.match.url);
    }
  }

  @bind async handleObjektAuswahlAufheben() {
    const { auftragId } = this.props.match.params;
    if ((this.auftragStore.auftrag) && (this.auftragStore.auftrag.objekte)) {
      for (let i = 0; i < this.auftragStore.auftrag.objekte.length; i++) {
        const response = await this.apiStore.auftragObjektEntfernen(auftragId, this.auftragStore.auftrag.objekte[i].objektguid);
        if (!response.ok) {
          break;
        }
      }
      this.loadAuftrag();
    }
  }

  render() {
    const { url } = this.props.match;
    const { auftrag } = this.auftragStore;

    return (
      <div className="auftrag-details">
        <BreadCrumbs />
        {!auftrag ? <LadeFortschritt/> : (
          <div>
            <h2>{auftrag.auftrag_name}</h2>
            <Switch>
              <PrivateRoute
                path={`${url}/vorgang/:vorgangId`}
                component={VorgangDetailRouter}
              />

              {/* @todo: kann weg? */}
              <PrivateRoute
                path={`${url}/auftraggeber`}
                render={() =>
                  <AuftragAnlegen
                  />}/>
              />

              <PrivateRoute path={`${url}/details/aendern`} render={() => (
                <AuftragDetailsAendern
                  auftrag={auftrag}
                  onAuftragNeuladen={this.handleAuftragNeuladen}
                />
              )} />

              <PrivateRoute path={`${url}/adresse/auftraggeber`} render={() => (
                <AuftragAdresseAendern
                  auftrag={auftrag}
                  caption="Auftraggeber ändern"
                  adressArt={AdressArt.Auftraggeber}
                  onAuftragNeuladen={this.handleAuftragNeuladen}
                />
              )} />

              <PrivateRoute path={`${url}/adresse/untersuchungsort`} render={() => (
                <AuftragAdresseAendern
                  auftrag={auftrag}
                  caption="Untersuchungsort ändern"
                  adressArt={AdressArt.Beauftragter}
                  onAuftragNeuladen={this.handleAuftragNeuladen}
                />
              )} />

              <PrivateRoute path={`${url}/adresse/kalibrierort`} render={() => (
                <AuftragAdresseAendern
                  auftrag={auftrag}
                  caption="Ort der Kalibrierung ändern"
                  adressArt={AdressArt.Beauftragter}
                  onAuftragNeuladen={this.handleAuftragNeuladen}
                />
              )} />

              <PrivateRoute path={`${url}/objekte`} exact={true} render={() => (
                <ObjektAuswahl kategorie={auftrag.kategorie} onObjektAuswahl={this.handleObjektAuswahl} />
              )} />

              <PrivateRoute path={`${url}/objekte/anlegen/ses`} exact={true} render={() => (
                <ObjektAnlegenSEP onAddObjekt={this.handleAddObjekt} />
              )} />

              <PrivateRoute path={`${url}/objekte/anlegen/agt`} exact={true} render={() => (
                <ObjektAnlegenAGT onAddObjekt={this.handleAddObjekt} />
              )} />

              <PrivateRoute path={`${url}/objekte/anlegen/bps`} exact={true} render={() => (
                <ObjektAnlegenBPS onAddObjekt={this.handleAddObjekt} />
              )} />

              <PrivateRoute
                path={`${url}/neuedienstleistung`}
                render={() =>
                  <AuftragDienstleistungAnlegen
                    auftragId={auftrag.anr}
                    auftragKategorie={auftrag.kategorie ? auftrag.kategorie : ''}
                    onAddDienstleistung={this.handleAddDienstleistung}
                  />}
              />
              <PrivateRoute render={() =>
                <AuftragDetails
                  auftragId={auftrag.anr}
                  onAuftragAbschliessen={this.handleAuftragAbschliessen}
                  onAuftragZurueckstellen={this.handleAuftragZurueckstellen}
                  onAuftragInArbeit={this.handleAuftragInArbeit}
                  onAuftragObjektAuswahlAufheben={this.handleObjektAuswahlAufheben}
                  onFolgeAuftragAnlegen={this.handleFolgeAuftragAnlegen}
                />}/>
            </Switch>
          </div>
        )}
      </div>
    );
  }

}




// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/auftraege/AuftragDetailRouter.tsx